import { useState, useCallback, useMemo } from 'react'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { VersionReportTemplateEntity } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { TemplateMenu } from './TemplateMenu'
import { ReportMenu } from './ReportMenu'
import { UseTemplateDataReturnValue } from '../../hooks/useTemplateData'
import { UseReportDataReturnValue } from '../../hooks/useReportData'
import { NewVersionDialog } from '../dialog/NewVersionDialog'
import { TemplateDialog } from '../dialog/TemplateDialog'
import { ReportDialog } from '../dialog/ReportDialog'
import Slide from '@mui/material/Slide'
import { styled } from '@mui/material/styles'
import { colorPalette } from '../../../../style/colorPalette'

interface VersionSelectMenuProps {
  projectUuid: string
  open: boolean
  onClose: () => void
  onReportSelect: (report: VersionReportVersionEntity) => void
  onAfterCreateReport: (uuid: string, isInitial: boolean) => void
  onAfterUpdateTemplate: () => Promise<void>
  onAfterUpdateReport: () => Promise<void>
  templateData: UseTemplateDataReturnValue
  reportData: UseReportDataReturnValue
}

export const VersionSelectMenu = ({
  projectUuid,
  open,
  onClose,
  onReportSelect,
  onAfterCreateReport,
  onAfterUpdateTemplate,
  onAfterUpdateReport,
  templateData,
  reportData,
}: VersionSelectMenuProps) => {
  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] =
    useState<boolean>(false)
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] =
    useState<boolean>(false)
  const [editingTemplate, setEditingTemplate] = useState<
    VersionReportTemplateEntity | undefined
  >()
  const [isReportDialogOpen, setIsReportDialogOpen] = useState<boolean>(false)
  const [editingReport, setEditingReport] = useState<
    VersionReportVersionEntity | undefined
  >()

  const {
    selectTemplate: selectTemplateData,
    resetTemplateSelection,
    selectedTemplate,
    loading: templateLoading,
    templates,
    refreshTemplates,
  } = templateData

  const { reports, loading: reportLoading, refreshReports } = reportData

  const openNewVersionDialog = useCallback(() => {
    setIsNewVersionDialogOpen(true)
  }, [])

  const closeNewVersionDialog = useCallback(() => {
    setIsNewVersionDialogOpen(false)
  }, [])

  const openTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(true)
  }, [])

  const closeTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(false)
    setEditingTemplate(undefined)
  }, [])

  const handleAfterCreateReportInternal = useCallback(
    async (
      uuid: string,
      _name: string,
      isInitial: boolean,
      _version: VersionReportVersionEntity
    ) => {
      await refreshReports()
      onAfterCreateReport(uuid, isInitial)
    },
    [onAfterCreateReport, refreshReports]
  )

  const handleTemplateUpdate = useCallback(
    (template: VersionReportTemplateEntity) => {
      setEditingTemplate(template)
      setIsTemplateDialogOpen(true)
    },
    []
  )

  const handleAfterSubmitTemplateInternal = useCallback(async () => {
    const wasEditing = editingTemplate !== undefined
    setIsTemplateDialogOpen(false)
    setEditingTemplate(undefined)
    await refreshTemplates()
    if (wasEditing) {
      await onAfterUpdateTemplate()
    }
  }, [editingTemplate, refreshTemplates, onAfterUpdateTemplate])

  const handleReportUpdate = useCallback(
    (report: VersionReportVersionEntity) => {
      setEditingReport(report)
      setIsReportDialogOpen(true)
    },
    []
  )

  const closeReportDialog = useCallback(() => {
    setIsReportDialogOpen(false)
    setEditingReport(undefined)
  }, [])

  const handleAfterSubmitReportInternal = useCallback(async () => {
    setIsReportDialogOpen(false)
    setEditingReport(undefined)
    await refreshReports()
    await onAfterUpdateReport()
  }, [refreshReports, onAfterUpdateReport])

  const isReportMode = useMemo(() => !!selectedTemplate, [selectedTemplate])

  return (
    <>
      <StyledCollapse in={open} direction="right">
        <DialogContainer>
          {isReportMode ? (
            <ReportMenu
              templateName={selectedTemplate?.name || ''}
              reports={reports}
              loading={reportLoading}
              onReportSelect={onReportSelect}
              onReportUpdate={handleReportUpdate}
              onBackToTemplate={resetTemplateSelection}
              onCreateNewReport={openNewVersionDialog}
              onClose={onClose}
            />
          ) : (
            <TemplateMenu
              templates={templates}
              loading={templateLoading}
              onTemplateSelect={selectTemplateData}
              onCreateNewTemplate={openTemplateDialog}
              onTemplateUpdate={handleTemplateUpdate}
              onClose={onClose}
            />
          )}
        </DialogContainer>
      </StyledCollapse>
      <NewVersionDialog
        open={isNewVersionDialogOpen}
        templateUuid={selectedTemplate?.uuid}
        onAfterSubmit={handleAfterCreateReportInternal}
        onClose={closeNewVersionDialog}
      />
      <TemplateDialog
        open={isTemplateDialogOpen}
        onAfterSubmit={handleAfterSubmitTemplateInternal}
        onClose={closeTemplateDialog}
        original={editingTemplate}
      />
      {editingReport && (
        <ReportDialog
          open={isReportDialogOpen}
          onAfterSubmit={handleAfterSubmitReportInternal}
          onClose={closeReportDialog}
          original={editingReport}
        />
      )}
    </>
  )
}

const DialogContainer = styled('div')({
  width: '480px',
  height: '100%',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

const StyledCollapse = styled(Slide)({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  height: '100%',
})
